// src/components/BusinessSuite/discounts/components/OfferingsSelector.jsx
import { useEffect, useMemo, useState } from 'react';
import { getOfferings } from '../utils/api';

const cache = new Map(); // key: `${kind}:${businessId}` -> raw data

export default function OfferingsSelector({
  kind,
  businessId,
  selected,
  setSelected,
  disabled,            // whole component disabled (hides it)
  disabledIds = [],    // NEW: array of item ids that should be disabled
}) {
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [q, setQ] = useState('');
  const cacheKey = `${kind}:${businessId ?? 'global'}`;

  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoading(true);
      try {
        if (cache.has(cacheKey)) {
          const data = cache.get(cacheKey);
          if (mounted) setGroups(normalize(data, kind));
        } else {
          const data = await getOfferings(kind, businessId);
          cache.set(cacheKey, data);
          if (mounted) setGroups(normalize(data, kind));
        }
      } catch (e) {
        console.error(e);
        if (mounted) setGroups([]);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, [cacheKey, kind, businessId]);

  const filtered = useMemo(() => {
    const qlc = q.trim().toLowerCase();
    if (!qlc) return groups;
    return groups
      .map(g => ({ ...g, items: g.items.filter(it => it.name.toLowerCase().includes(qlc)) }))
      .filter(g => g.items.length > 0);
  }, [groups, q]);

  function toggle(id) {
    if (disabledIds?.includes(id)) return; // ignore clicks on disabled items
    setSelected(selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]);
  }

  if (disabled) return null;

  return (
    <div className="grid gap-4">
      {kind === 'products' && (
        <input
          type="text"
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Search products by name…"
          className="w-full rounded border px-2 py-1"
        />
      )}

      {loading ? (
        <p>Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-gray-500">No items found.</p>
) : (
  <>
    {filtered.map((group, gi) => {
      const groupKey = group.id ?? `${group.category ?? 'group'}::${gi}`;
      return (
        <div key={groupKey}>
          <h4 className="mb-2 font-semibold capitalize">{group.category}</h4>
          <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3">
            {group.items.map((item, ii) => {
              const isDisabled = disabledIds?.includes(item.id);
              const itemKey = item.id ?? `${groupKey}::item::${ii}`;
              return (
                <label
                  key={itemKey}
                  className={`flex items-center gap-2 ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <input
                    type="checkbox"
                    className="checkbox checkbox-sm"
                    checked={selected.includes(item.id)}
                    onChange={() => toggle(item.id)}
                    disabled={isDisabled}
                  />
                  <span>{item.name}</span>
                </label>
              );
            })}
          </div>
        </div>
      );
    })}
  </>
)}
    </div>
  );
}
function normalize(data, kind) {
  if (Array.isArray(data) && data.length && data[0]?.items) {
    // Ensure each group has a unique 'id'
    return data.map((g, i) => ({
      id: g.id ?? g.slug ?? `${g.category ?? 'group'}::${i}`,
      category: g.category ?? 'uncategorised',
      items: (g.items ?? []).map(it => ({ id: it.id, name: it.name })),
    }));
  }
  if (kind === 'products' && Array.isArray(data)) {
    return [{
      id: 'all-products',
      category: 'all products',
      items: data.map(p => ({ id: p.id, name: p.name })),
    }];
  }
  return [];
}
